import _ from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Loader, Popup, Segment, Table } from "semantic-ui-react";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { BOX_LOWER_FILL_COLOR, BOX_UPPER_FILL_COLOR, TRANSPARENT_COLOR } from "../../../../utilities/graphics";
import * as outlierFunctions from "../../../Lumber/DataAnalysisCriteria/outlierFunctions";
import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import "./style.css";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as PropTypes from "prop-types";
import { getPlotAnalysisResultsForBoxWhisker } from "../../../../apis/analysisApi";
import AnalysisTopBar from "../../../Lumber/AnalysisTopBar";
import { useTrialAnalysis } from "../../Trials/TrialAnalysis/TrialAnalysisContext";
import { getIsPlotUniformity } from "../../Trials/TrialAnalysis/utils";
import { PlotlyIcons } from "./plotlyIcons";
import { fetchTrialData } from "../../../../apis/trialApi";
import { AXIS_TICK_SIZE, AXIS_TITLE_SIZE } from "./constants";
import { getColorFromPalette } from "../../Trials/MFASummary/MapStyles";

const AnalysisBoxWhiskerChart = ({ trialOptions }) => {
	const entityNames = useSelector((state) => state.entityNames ?? null);
	const moduleNavigation = useModuleNavigation();
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();

	const { committed, pending, display, isInitialized } = useTrialAnalysis();

	//--UI Control
	const [disableColors, setDisableColors] = useState(false);
	const [showTreatmentNames, setShowTreatmentNames] = useState(false);
	const [stagedGroupPlots, setGroupPlots] = useState(undefined);

	const groupPlots = stagedGroupPlots ?? display.isRCBD;

	const isPlotUniformity = useMemo(
		() => getIsPlotUniformity(trialOptions, pending.analysisTypeId),
		[trialOptions, pending.analysisTypeId],
	);

	const { data: trialData } = useQuery({
		queryKey: ["fetch trial data", userAuth.currentClientId, moduleNavigation.trialId],
		queryFn: async () => fetchTrialData(userAuth.currentClientId, moduleNavigation.trialId, await getTokenSilently()),
		select: useCallback(
			(data) => {
				if (!display.treatmentNames || display.treatmentNames.length === 0) return data;
				return {
					...data,
					treatments: _.map(display.treatmentNames, (tn, idx) => ({
						treatmentId: _.uniqueId(),
						trialTreatmentId: idx,
						treatmentName: tn,
						treatmentDescription: null,
						treatmentTypeId: null,
						treatmentTypeName: null,
						treatmentTypeDescription: null,
						seed: null,
					})),
				};
			},
			[display.treatmentNames],
		),
		enabled: !!userAuth.currentClient && !!moduleNavigation.trialId && !display.treatmentNames?.length,
		staleTime: Infinity,
		placeholderData: keepPreviousData,
	});

	const { data: stagedData, isPending } = useQuery({
		queryKey: [
			"plot analysis for box whisker",
			committed.meanComparisonOptionId,
			committed.alphaOptionId,
			committed.removeDamagedOrExcluded,
			committed.removeOutliers,
			committed.outlierType,
			pending.analysisId,
			pending.flightId,
			pending.quantifiedRegionTypeId,
			pending.analysisTypeId,
			isPlotUniformity,
			pending.groundDataAssessmentId,
			pending.groundDatasetId,
			pending.curveModelAnalysisId,
			pending.experimentalDesignId,
			userAuth.currentClientId,
			moduleNavigation.trialId,
		],
		queryFn: async () =>
			getPlotAnalysisResultsForBoxWhisker(
				userAuth.currentClientId,
				moduleNavigation.trialId,
				pending.analysisId,
				pending.flightId,
				pending.quantifiedRegionTypeId,
				pending.analysisTypeId,
				isPlotUniformity,
				pending.groundDataAssessmentId,
				pending.groundDatasetId,
				pending.curveModelAnalysisId,
				committed.meanComparisonOptionId,
				committed.alphaOptionId,
				await getTokenSilently(),
				committed.removeDamagedOrExcluded,
				committed.removeOutliers,
				committed.outlierType,
				pending.experimentalDesignId,
			),
		enabled: isInitialized && !!userAuth.currentClientId && !!moduleNavigation.trialId,
		select: useCallback(
			(data) => {
				if (!data) return [];
				return display.experimentalDesignId ? data[display.experimentalDesignId] : _.values(data)?.[0];
			},
			[display.experimentalDesignId],
		),
		staleTime: Infinity,
	});

	const data = useMemo(
		() =>
			display.experimentalDesignId && stagedData?.length
				? groupPlots
					? _.chain(stagedData)
							.groupBy((plot) => `${plot.plotGroup}`)
							.mapValues((plotGroup) => ({
								...plotGroup[0],
								plotName: plotGroup[0].plotGroup,
								plotAnalysisResultValue: _.meanBy(plotGroup, "plotAnalysisResultValue"),
							}))
							.flatMap()
							.value()
					: stagedData
				: (stagedData ?? []),
		[stagedData, display, groupPlots],
	);

	const treatmentNameMap = useMemo(() => {
		if (display.treatmentNames) {
			return _.reduce(
				display.treatmentNames,
				(acc, curr, idx) => {
					acc[String(idx)] = curr;
					return acc;
				},
				{},
			);
		} else {
			const map = {};
			(trialData?.treatments ?? []).forEach((t) => {
				if (t.treatmentName) map[String(t.trialTreatmentId)] = t.treatmentName;
			});
			return map;
		}
	}, [display.treatmentNames, trialData?.treatments]);

	const filteredAnalysisResults = useMemo(() => {
		const countTreatments = _.uniqBy(data, (td) => td.trialTreatmentId).length;

		return _.groupBy(
			_.filter(data, (td) => !td.excludeFromAssessment && (countTreatments === 1 || td.trialTreatmentId !== 0)),
			"trialTreatmentId",
		);
	}, [data]);

	const hasNoData = !data.length;

	const anovaTableData = useMemo(() => {
		if (!data) return null;
		const first = data[0];

		if (!first || !first.pValue) return null;

		return {
			cv: Number(first.cv),
			df: Number(first.df),
			fValue: Number(first.fValue),
			pValue: Number(first.pValue),
		};
	}, [data]);

	const { bottomExtra, chartOptions } = useMemo(() => {
		if (!data.length) return { bottomExtra: 0, chartOptions: {} };

		let scatterPlots = {};
		let boxX = [];
		let boxMin = [],
			boxQ1 = [],
			boxMedian = [],
			boxQ3 = [],
			boxMax = [];

		_.forEach(filteredAnalysisResults, (results, index) => {
			let valuesWithMetaData =
				results[0].flightId == null
					? results[0].curveModelName
						? _.map(results, (r) => ({ value: r.plotAnalysisResultValue, plotName: r.plotName, rep: r.plotReplicate }))
						: _.map(results, (r) => ({
								value: r.groundDatasetAssessmentValue,
								plotName: r.plotName,
								rep: r.plotReplicate,
							}))
					: _.map(results, (r) => ({ value: r.plotAnalysisResultValue, plotName: r.plotName, rep: r.plotReplicate }));

			valuesWithMetaData = _.filter(valuesWithMetaData, (vm) => vm.value !== null);
			const values = _.map(valuesWithMetaData, "value");

			const median = outlierFunctions.getMedian(values);
			const q1 = outlierFunctions.getQ1(values);
			const q3 = outlierFunctions.getQ3(values);
			const min = Number(Math.min(...values).toFixed(5));
			const max = Number(Math.max(...values).toFixed(5));

			const numLabel = String(parseInt(index));
			boxX.push(numLabel);
			boxMin.push(min);
			boxQ1.push(q1);
			boxMedian.push(median);
			boxQ3.push(q3);
			boxMax.push(max);

			const repsValuesWithMetaData = _.groupBy(valuesWithMetaData, "rep");
			_.map(repsValuesWithMetaData, (rvm, rep) => {
				_.map(rvm, (vm) => {
					if (!scatterPlots[rep]) scatterPlots[rep] = [];
					scatterPlots[rep].push({
						x: numLabel,
						y: vm.value,
						plotName: vm.plotName,
						treatmentNum: parseInt(index),
					});
				});
			});
		});

		const statsMap = {};
		boxX.forEach((cat, i) => {
			statsMap[cat] = { min: boxMin[i], q1: boxQ1[i], median: boxMedian[i], q3: boxQ3[i], max: boxMax[i] };
		});

		let letteringLabels = _.map(filteredAnalysisResults, (results) => ({ label: results[0].letter }));
		const allMeanComparisonLetterA = letteringLabels.every((ll) => ll.label && ll.label !== "a");
		if (allMeanComparisonLetterA) {
			letteringLabels.forEach((ll) => (ll.label = "-"));
		}

		const letterAnnotations = letteringLabels.map((ll, i) => ({
			x: i,
			y: 1.06,
			yref: "paper",
			text: ll.label ?? "",
			showarrow: false,
			font: { size: AXIS_TICK_SIZE },
			textangle: -45,
		}));

		const numTreatments = letterAnnotations.length;
		const maxLabelLen = Math.max(1, ...letterAnnotations.map((a) => (a.text ?? "").trim().length));
		const tooMany = maxLabelLen * numTreatments > 140;
		if (tooMany) {
			letterAnnotations.forEach((a) => {
				a.text = "-";
			});
		}
		const annotationY = tooMany ? 1.06 : 1.06 + Math.max(0, (maxLabelLen - 2) * 0.015);
		const marginTop = tooMany ? 90 : 90 + Math.max(0, (maxLabelLen - 2) * 10);
		letterAnnotations.forEach((a) => {
			a.y = annotationY;
		});

		const maxNameLength = showTreatmentNames
			? Math.max(0, ...boxX.map((cat) => (treatmentNameMap[cat] || "").length))
			: 0;

		const nameAnnotations = showTreatmentNames
			? boxX
					.map((cat, i) => {
						const name = treatmentNameMap[cat];
						if (!name) return null;
						return {
							x: i,
							y: -0.12,
							yref: "paper",
							xanchor: "center",
							yanchor: "top",
							text: name,
							showarrow: false,
							font: { size: AXIS_TICK_SIZE },
							textangle: -45,
						};
					})
					.filter(Boolean)
			: [];

		// Box is never thinner than a scatter marker (8px) at the minimum expected chart width (600px)
		// const scatterDataWidth = (8 / 600) * boxX.length;
		// const boxWidth = Math.min(0.6, Math.max(0.2, scatterDataWidth));

		const boxBaseProps = {
			type: "box",
			x: boxX,
			boxpoints: false,
			showlegend: false,
			// width: boxWidth,
		};

		const traces = [
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxQ1,
				q3: boxMedian,
				upperfence: boxMedian,
				fillcolor: BOX_LOWER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMedian,
				q1: boxMedian,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: BOX_UPPER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: TRANSPARENT_COLOR,
				line: { color: "black" },
				hoverinfo: "skip",
			},
		];

		for (let [rep, sp] of Object.entries(scatterPlots)) {
			const marker = { color: getColorFromPalette(!disableColors ? Number(rep) - 1 : 0), shape: "circle" };
			traces.push({
				type: "box",
				// mode: "markers",
				boxpoints: "all",
				jitter: groupPlots ? 0 : 1,
				pointpos: 0,
				fillcolor: "rgba(255,255,255,0)",
				line: {
					color: "rgba(255,255,255,0)",
				},
				name: `${rep}`,
				x: sp.map((p) => p.x),
				y: sp.map((p) => p.y),
				customdata: sp.map((p) => {
					const s = statsMap[p.x];
					return [p.plotName, p.treatmentNum, rep, s.min, s.q1, s.median, s.q3, s.max];
				}),
				hovertemplate:
					"<b>Plot: </b>%{customdata[0]}<br>" +
					"<b>Treatment: </b>%{customdata[1]}<br>" +
					"<b>Replicate: </b>%{customdata[2]}<br>" +
					"<b>Value: </b>%{y}<br>" +
					"<br>" +
					"<b>Min: </b>%{customdata[3]}<br>" +
					"<b>Q1: </b>%{customdata[4]}<br>" +
					"<b>Median: </b>%{customdata[5]}<br>" +
					"<b>Q3: </b>%{customdata[6]}<br>" +
					"<b>Max: </b>%{customdata[7]}<extra></extra>",
				marker: {
					color: marker.color,
					symbol: marker.shape,
					size: groupPlots ? 8 : 6,
					line: { color: "black", width: 0.5 },
				},
				showlegend: !disableColors,
			});
		}

		const layout = {
			xaxis: {
				title: {
					text: "Treatment",
					standoff: showTreatmentNames ? 80 + maxNameLength * 5 : 5,
					font: { size: AXIS_TITLE_SIZE },
				},
				tickfont: { size: AXIS_TICK_SIZE },
				type: "category",
				tickangle: 0,
				automargin: true,
				// range: [-0.3, boxX.length - 0.7],
			},
			yaxis: {
				title: { text: display.assessmentName, standoff: 10, font: { size: AXIS_TITLE_SIZE } },
				tickfont: { size: AXIS_TICK_SIZE },
				zeroline: false,
				automargin: true,
			},
			legend: {
				orientation: "h",
				title: { text: "Replicates" },
				x: 1,
				xanchor: "right",
				y: showTreatmentNames ? -(0.18 + maxNameLength * 0.009) : -0.2,
				yanchor: "top",
			},
			margin: { l: 60, r: 20, b: 80 + (showTreatmentNames ? 50 + maxNameLength * 5 : 0), t: marginTop, pad: 0 },
			hovermode: "closest",
			autosize: true,
			annotations: [...letterAnnotations, ...nameAnnotations],
			boxmode: "overlay",
			boxgap: 0,
		};

		return { bottomExtra: showTreatmentNames ? 50 + maxNameLength * 5 : 0, chartOptions: { data: traces, layout } };
	}, [
		display.assessmentName,
		data,
		disableColors,
		filteredAnalysisResults,
		groupPlots,
		showTreatmentNames,
		treatmentNameMap,
	]);

	const modeBarButtons = useMemo(() => {
		const buttons = [];
		if (_.entries(treatmentNameMap).length) {
			buttons.push({
				name: "toggle treatment names",
				title: "Toggle Treatment Names",
				icon: PlotlyIcons.title,
				attr: showTreatmentNames ? "names-active" : "names-inactive",
				click: () => {
					setShowTreatmentNames(!showTreatmentNames);
				},
			});
		}

		if (entityNames?.fieldScaleTrial) {
			buttons.push({
				name: "toggle treatment blocks",
				title: "Toggle Treatment Blocks",
				icon: PlotlyIcons.treatmentBlocks,
				attr: groupPlots ? "blocks-active" : "blocks-inactive",
				click: () => {
					setGroupPlots(!groupPlots);
				},
			});
		}

		buttons.push({
			name: "toggle chart colors",
			title: "Toggle Chart Colors",
			icon: PlotlyIcons.pallette,
			attr: disableColors ? "colors-inactive" : "colors-active",
			click: () => {
				setDisableColors(!disableColors);
			},
		});
		return buttons;
	}, [disableColors, entityNames?.fieldScaleTrial, groupPlots, showTreatmentNames, treatmentNameMap]);

	return (
		<>
			<Segment basic style={{ padding: 0, marginTop: 0 }}>
				<AnalysisTopBar options={trialOptions} mode="boxWhisker" />
			</Segment>
			{isPending ? (
				<Loader active inline="centered" />
			) : data.length === 0 ? (
				<Segment>
					<p>{"We could not find analysis data for this trial."}</p>
				</Segment>
			) : (
				<Segment basic loading={!chartOptions.data} className="boxWhiskerChartSegment">
					{!!anovaTableData && (
						<Popup
							trigger={
								<span
									className="popupTriggerText"
									style={{ position: "absolute", top: "1em", left: "1em", zIndex: "1" }}
								>
									ANOVA Table
								</span>
							}
							position="bottom left"
							on={"click"}
							content={
								<div className="aucTableContainer" style={{ padding: 0 }}>
									<Table basic="very" celled>
										<Table.Header>
											<Table.Row style={{ whiteSpace: "nowrap" }}>
												<Table.HeaderCell style={{ borderTop: "none", textAlign: "center" }}></Table.HeaderCell>
												<Table.HeaderCell style={{ borderTop: "none", textAlign: "center" }}>Avg CV</Table.HeaderCell>
												<Table.HeaderCell style={{ borderTop: "none", textAlign: "center" }}>DF</Table.HeaderCell>
												<Table.HeaderCell style={{ borderTop: "none", textAlign: "center" }}>F-Value</Table.HeaderCell>
												<Table.HeaderCell style={{ borderTop: "none", textAlign: "center" }}>p-Value</Table.HeaderCell>
											</Table.Row>
										</Table.Header>
										<Table.Body>
											<Table.Row>
												<Table.Cell>
													<strong>Treatment</strong>
												</Table.Cell>
												<Table.Cell className="aucTableNumber" style={{ textAlign: "right" }}>
													{anovaTableData.cv.toFixed(4)}
												</Table.Cell>
												<Table.Cell className="aucTableNumber" style={{ textAlign: "right" }}>
													{anovaTableData.df.toFixed(1)}
												</Table.Cell>
												<Table.Cell className="aucTableNumber" style={{ textAlign: "right" }}>
													{anovaTableData.fValue.toFixed(2)}
												</Table.Cell>
												<Table.Cell className="aucTableNumber" style={{ textAlign: "right" }}>
													{anovaTableData.pValue.toFixed(4) !== "0.00000"
														? anovaTableData.pValue.toFixed(4)
														: "<0.0001"}
												</Table.Cell>
											</Table.Row>
										</Table.Body>
									</Table>
								</div>
							}
							basic
						/>
					)}
					{hasNoData ? (
						<p>{"No Data for this assessment combination."}</p>
					) : (
						<AFPlot
							data={chartOptions.data}
							layout={chartOptions.layout}
							config={{
								displayModeBar: true,
								modeBarButtonsToAdd: modeBarButtons,
								toImageButtonOptions: {
									filename: `${entityNames?.trialName}_${/\(([^)]+)\)/.exec(display.assessmentName)?.[1] ?? display.assessmentName}_BoxWhisker`,
								},
							}}
							useResizeHandler
							style={{ width: "100%", height: bottomExtra > 0 ? `calc(70vh + ${bottomExtra}px)` : "70vh" }}
						/>
					)}
				</Segment>
			)}
		</>
	);
};

AnalysisBoxWhiskerChart.propTypes = {
	trialOptions: PropTypes.object,
};

export default AnalysisBoxWhiskerChart;
